/**
 * baseGraphAtoms - Source atoms for the base graph produced by the user's Cypher query.
 *
 * The base graph is the set of nodes and edges returned from the initial query,
 * before any focus-mode exploration is applied. These atoms are written to when
 * a new query result arrives and read by derived atoms to compute the visible graph.
 */

import type { Node, Edge } from '@xyflow/react';
import { atom, type Atom, type PrimitiveAtom } from 'jotai';

import type { ObjectEdgeData } from '../nodes/ObjectEdge';
import type { ObjectNodeData } from '../nodes/ObjectNode';

// ============================================================================
// Source atoms
// ============================================================================

/** Nodes from the user's original Cypher query result */
export const BaseNodesAtom: PrimitiveAtom<Node<ObjectNodeData>[]> & {
	init: Node<ObjectNodeData>[];
} = atom<Node<ObjectNodeData>[]>([]);

/** Edges from the user's original Cypher query result */
export const BaseEdgesAtom: PrimitiveAtom<Edge<ObjectEdgeData>[]> & {
	init: Edge<ObjectEdgeData>[];
} = atom<Edge<ObjectEdgeData>[]>([]);

/**
 * Whether focus-mode exploration should render one node per canonical entity ID.
 *
 * The general Graph Explorer keeps a dimmed base node and an active explored copy
 * so query-result context remains visible. Embedded graphs that seed already-known
 * relationships can opt into canonical identity instead: selecting one of those
 * relationships should reposition the existing entity, not render it a second time.
 */
export const DeduplicateExploredBaseNodesAtom: PrimitiveAtom<boolean> & { init: boolean } =
	atom(false);

/** Set of node IDs that belong to the base graph (not explored children).
 *  Only includes actual object nodes — excludes group rectangles and header annotations.
 */
export const BaseNodeIdsAtom: Atom<Set<string>> = atom<Set<string>>((get) => {
	return new Set(
		get(BaseNodesAtom)
			.filter((n) => n.type === 'objectNode')
			.map((n) => n.id),
	);
});
